import { Logtail } from "@logtail/browser";

const sourceToken = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN;
const enabled = import.meta.env.VITE_BETTERSTACK_ENABLED === 'true';
// Хост без протоколу (напр. "s123.eu-fsn-3.betterstackdata.com") браузер трактує як відносний шлях - логи йшли на
// localhost/<сторінка>/... і отримували 404. Без протоколу - https:// (2026-09-30).
const rawIngestingHost: string | undefined = import.meta.env.VITE_BETTERSTACK_INGESTING_HOST;
const ingestingHost = rawIngestingHost && !/^https?:\/\//.test(rawIngestingHost) ? `https://${rawIngestingHost}` : rawIngestingHost;
const logLevel = (import.meta.env.VITE_BETTERSTACK_LOG_LEVEL || 'info') as 'debug' | 'info' | 'warn' | 'error';

let logtail: Logtail | null = null;

const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const shouldLog = (level: keyof typeof LOG_LEVELS) => {
  return LOG_LEVELS[level] >= LOG_LEVELS[logLevel];
};

// Чутливі поля - маскуються перед відправкою в BetterStack (2026-09-25): помилка axios серіалізувалась разом із
// config.data - тілом запиту, де міг бути пароль. Точні назви, не "містить" - statusCode/errorCode лишаються.
const SENSITIVE_KEY = /^(.*password|.*token|.*secret|authorization|otp|code|.*verificationcode|.*resetcode|pin|cvv|cvc|cardnumber)$/i;

const sanitizeReplacer = (key: string, value: unknown) =>
  key && SENSITIVE_KEY.test(key.replace(/[_-]/g, '')) ? '***' : value;

/**
 * Один аргумент console.* -> рядок для BetterStack (2026-09-25). Раніше все йшло через JSON.stringify: Error і Event
 * (їхні поля не enumerable) ставали "{}" - у логах з прод-вебу були десятки помилок з порожнім message. Тепер:
 * Error -> "Name: message" + stack; помилка axios -> метод, URL, статус і відповідь (без config з тілом і заголовками);
 * Event -> тип події; решта - JSON з замаскованими чутливими полями.
 */
const formatLogArg = (arg: any): string => {
  if (arg === null || arg === undefined || typeof arg !== 'object') return String(arg);
  if (arg?.isAxiosError) {
    const { config, response, message } = arg;
    return `AxiosError: ${message} [${config?.method?.toUpperCase() ?? '?'} ${config?.url ?? '?'} ${response?.status ?? 'no response'}] ${
      JSON.stringify(response?.data ?? null, sanitizeReplacer)
    }`;
  }
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  if (typeof Event !== 'undefined' && arg instanceof Event) return `[Event ${arg.type}]`;
  try {
    const json = JSON.stringify(arg, sanitizeReplacer);
    return json === '{}' ? `[${arg.constructor?.name ?? 'Object'}]` : json;
  } catch {
    return String(arg);
  }
};

export const initLogger = () => {
  if (enabled && sourceToken) {
    try {
      logtail = new Logtail(sourceToken, {
        endpoint: ingestingHost,
      });

      const originalConsoleLog = console.log;
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      const originalConsoleInfo = console.info;
      const originalConsoleDebug = console.debug;

      const formatArgs = (args: any[]) => args.map(formatLogArg).join(' ');

      console.log = (...args: any[]) => {
        originalConsoleLog(...args);
        // console.log зазвичай вважається info або debug, тут прирівняємо до info
        if (shouldLog('info')) {
            logtail?.info(formatArgs(args));
        }
      };

      console.info = (...args: any[]) => {
        originalConsoleInfo(...args);
        if (shouldLog('info')) {
            logtail?.info(formatArgs(args));
        }
      };

      console.warn = (...args: any[]) => {
        originalConsoleWarn(...args);
        if (shouldLog('warn')) {
            logtail?.warn(formatArgs(args));
        }
      };

      console.error = (...args: any[]) => {
        originalConsoleError(...args);
        if (shouldLog('error')) {
            logtail?.error(formatArgs(args));
        }
      };
      
      console.debug = (...args: any[]) => {
        originalConsoleDebug(...args);
        if (shouldLog('debug')) {
            logtail?.debug(formatArgs(args));
        }
      };

      console.log("[Logger] Better Stack logger initialized with level:", logLevel);
    } catch (error) {
      console.error("[Logger] Failed to initialize Better Stack logger", error);
    }
  } else {
    console.log("[Logger] Better Stack logger disabled or missing token");
  }
};

export default logtail;